import { useParams, Link } from 'react-router-dom';
import { MapPin, Globe, Building2, Users, Edit, Mail, ExternalLink, Briefcase } from 'lucide-react';
import { useClientProfile } from '@/lib/hooks/use-client-profile';
import { AnimatedLoader } from '@/components/ui/animated-loader';

export default function ClientProfile() {
  const { id } = useParams<{ id: string }>();
  const { clientProfile: profile, isLoading, error, isOwner } = useClientProfile(id);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <AnimatedLoader message="Loading profile..." />
      </div>
    );
  }

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-[1200px] mx-auto px-8 py-16">
          <div className="bg-white rounded-[40px] border border-slate-200 p-12 text-center">
            <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-4">
              {errorMessage === 'Profile not found' ? 'Profile Not Found' : 'Error Loading Profile'}
            </h1>
            <p className="text-slate-500 mb-6">
              {errorMessage === 'Profile not found'
                ? "The company profile you're looking for doesn't exist."
                : `Error: ${errorMessage}`}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-medium text-base"
              style={{
                background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
              }}
            >
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-[1200px] mx-auto px-8 py-16">
          <div className="bg-white rounded-[40px] border border-slate-200 p-12 text-center">
            <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Profile Not Found</h1>
            <p className="text-slate-500 mb-6">The company profile you're looking for doesn't exist.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-medium text-base"
              style={{
                background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
              }}
            >
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const initials = profile.company_name
    ?.split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  const companySizeLabels: Record<string, string> = {
    startup: 'Startup (1-10)',
    small: 'Small (11-50)',
    medium: 'Medium (51-200)',
    enterprise: 'Enterprise (200+)'
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div
        className="py-12 px-8"
        style={{
          background: 'linear-gradient(180deg, rgba(232, 240, 254, 1) 0%, rgba(240, 245, 255, 1) 50%, rgba(248, 250, 252, 1) 100%)',
        }}
      >
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center gap-2 mb-6">
            <Building2 className="h-5 w-5 text-[#4669ed]" />
            <span className="text-[#4669ed] font-semibold text-sm">Company Profile</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1200px] mx-auto px-8 -mt-4 pb-16">
        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-[320px] flex-shrink-0 space-y-5">
            {/* Profile Card */}
            <div
              className="rounded-[40px] border border-slate-200 p-8"
              style={{
                background: 'linear-gradient(158.39deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 255, 1) 100%)',
              }}
            >
              {/* Avatar */}
              <div className="flex flex-col items-center mb-6">
                <div
                  className="w-[100px] h-[100px] rounded-[40px] flex items-center justify-center mb-4 overflow-hidden"
                  style={{
                    background: profile.profile_picture_url
                      ? 'transparent'
                      : 'linear-gradient(135deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                    boxShadow: '0px 4px 12px 0px rgba(70, 105, 237, 0.2)',
                  }}
                >
                  {profile.profile_picture_url ? (
                    <img
                      src={profile.profile_picture_url}
                      alt={profile.company_name || 'Company'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white font-black text-3xl">{initials}</span>
                  )}
                </div>

                <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
                  {profile.company_name}
                </h1>

                {profile.industry && (
                  <p className="text-slate-500 text-center">{profile.industry}</p>
                )}

                {/* Location */}
                {profile.location && (
                  <div className="flex items-center gap-1 text-slate-500 mt-2">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{profile.location}</span>
                  </div>
                )}
              </div>

              {/* Company Size Badge */}
              {profile.company_size && (
                <div className="flex justify-center mb-6">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(70,105,237,0.10)] text-[#4669ed] font-semibold text-sm">
                    <Users className="w-4 h-4" />
                    {companySizeLabels[profile.company_size] || profile.company_size}
                  </span>
                </div>
              )}

              {/* Edit Button for Owner */}
              {isOwner && (
                <Link
                  to="/client/profile"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-medium text-sm"
                  style={{
                    background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                  }}
                >
                  <Edit className="w-4 h-4" />
                  Edit Profile
                </Link>
              )}
            </div>

            {/* Links & Contact Card */}
            {(profile.website_url || profile.email) && (
              <div
                className="rounded-[40px] border border-slate-200 p-8"
                style={{
                  background: 'linear-gradient(158.39deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 255, 1) 100%)',
                }}
              >
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#4669ed]" />
                  Links & Contact
                </h3>
                <div className="space-y-3">
                  {profile.website_url && (
                    <a
                      href={profile.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-[16px] bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-[12px] bg-[rgba(70,105,237,0.10)] flex items-center justify-center">
                        <ExternalLink className="w-5 h-5 text-[#4669ed]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900">Website</div>
                        <div className="text-xs text-slate-500 truncate">
                          {profile.website_url.replace(/^https?:\/\//, '')}
                        </div>
                      </div>
                    </a>
                  )}
                  {profile.email && (
                    <a
                      href={`mailto:${profile.email}`}
                      className="flex items-center gap-3 p-3 rounded-[16px] bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-[12px] bg-[rgba(70,105,237,0.10)] flex items-center justify-center">
                        <Mail className="w-5 h-5 text-[#4669ed]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900">Email</div>
                        <div className="text-xs text-slate-500 truncate">{profile.email}</div>
                      </div>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-6">
            {/* About Section */}
            {profile.bio && (
              <div
                className="rounded-[40px] border border-slate-200 p-8"
                style={{
                  background: 'linear-gradient(158.39deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 255, 1) 100%)',
                }}
              >
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#4669ed]" />
                  About
                </h2>
                <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {profile.bio}
                </p>
              </div>
            )}

            {/* Company Details Section */}
            <div
              className="rounded-[40px] border border-slate-200 p-8"
              style={{
                background: 'linear-gradient(158.39deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 255, 1) 100%)',
              }}
            >
              <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#4669ed]" />
                Company Details
              </h2>
              <div className="grid grid-cols-2 gap-6">
                {profile.industry && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-[16px] bg-[rgba(70,105,237,0.10)] flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-6 h-6 text-[#4669ed]" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Industry</div>
                      <div className="text-lg font-medium text-slate-900">{profile.industry}</div>
                    </div>
                  </div>
                )}

                {profile.company_size && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-[16px] bg-[rgba(70,105,237,0.10)] flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-[#4669ed]" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Company Size</div>
                      <div className="text-lg font-medium text-slate-900">
                        {companySizeLabels[profile.company_size] || profile.company_size}
                      </div>
                    </div>
                  </div>
                )}

                {profile.location && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-[16px] bg-[rgba(70,105,237,0.10)] flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#4669ed]" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Location</div>
                      <div className="text-lg font-medium text-slate-900">{profile.location}</div>
                    </div>
                  </div>
                )}

                {profile.website_url && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-[16px] bg-[rgba(70,105,237,0.10)] flex items-center justify-center flex-shrink-0">
                      <Globe className="w-6 h-6 text-[#4669ed]" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Website</div>
                      <a
                        href={profile.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-lg font-medium text-[#4669ed] hover:underline"
                      >
                        {profile.website_url.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Empty State for Missing Info */}
            {!profile.bio && !profile.industry && !profile.company_size && (
              <div
                className="rounded-[40px] border border-slate-200 p-12 text-center"
                style={{
                  background: 'linear-gradient(158.39deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 255, 1) 100%)',
                }}
              >
                <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Profile Incomplete</h3>
                <p className="text-slate-500">
                  {isOwner
                    ? 'Complete your profile to help AI experts understand your company and needs.'
                    : 'This company has not completed their profile yet.'}
                </p>
                {isOwner && (
                  <Link
                    to="/client/profile"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium text-sm mt-6"
                    style={{
                      background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                    }}
                  >
                    <Edit className="w-4 h-4" />
                    Complete Profile
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
